import React, { useState, useEffect, useCallback, useRef } from "react";

/* ── Types ── */

type Category = "all" | "dashboards" | "gestion" | "facturation" | "donnees" | "communication";

interface Screenshot {
    id: string;
    file: string;
    title: string;
    description: string;
    category: Exclude<Category, "all">;
}

/* ── Screenshot data ── */

const SCREENSHOTS: Screenshot[] = [
    // Tableaux de bord
    {
        id: "admin-dashboard",
        file: "capture-03.png",
        title: "Tableau de bord administrateur",
        description: "Vue d'ensemble avec accès rapide à toutes les fonctionnalités : message du jour, pointage, feuille du mois, familles, enfants, réservations, facturation et emailings.",
        category: "dashboards",
    },
    {
        id: "parent-dashboard",
        file: "capture-02.png",
        title: "Page d'accueil parent",
        description: "Interface parent avec messages importants et accès direct aux fonctionnalités : famille, menu, réservation, facturation, documents et aide.",
        category: "dashboards",
    },

    // Gestion quotidienne
    {
        id: "pointage-du-jour",
        file: "capture-07.png",
        title: "Pointage du jour",
        description: "Suivi de présence quotidien par classe. Pour chaque enfant : absent justifié, non justifié ou présent. Totaux automatiques par section.",
        category: "gestion",
    },
    {
        id: "feuille-mois",
        file: "capture-08.png",
        title: "Feuille du mois",
        description: "Vue mensuelle des présences par classe avec cases à cocher pour chaque jour d'école. Totaux par section et nombre total de repas calculés automatiquement.",
        category: "gestion",
    },
    {
        id: "feuille-mois-pdf",
        file: "feuille-mois-pdf.png",
        title: "Export PDF de la feuille du mois",
        description: "La feuille du mois exportable en PDF, prête à imprimer. Même mise en page que la version en ligne, formatée pour un rendu papier professionnel.",
        category: "gestion",
    },
    {
        id: "feuille-mois-papier",
        file: "feuille-mois-papier.jpg",
        title: "Avant : la feuille papier",
        description: "L'ancienne méthode de suivi manuscrit des présences. Risques d'erreurs, temps de comptage important et aucune sauvegarde automatique.",
        category: "gestion",
    },

    // Réservations & Facturation
    {
        id: "reservations",
        file: "capture-16.png",
        title: "Liste des réservations",
        description: "Tableau des réservations avec statut, nom, classe, date et lien direct vers la facture. Filtres, tri et export Excel disponibles.",
        category: "facturation",
    },
    {
        id: "facturation-auto",
        file: "capture-01.png",
        title: "Facturation automatique",
        description: "Génération des factures en un clic pour toutes les dates sélectionnées. Récapitulatif avec le nombre de factures créées et le montant total.",
        category: "facturation",
    },
    {
        id: "facture-detail",
        file: "capture-17.png",
        title: "Détail d'une facture",
        description: "Vue complète d'une facture : destinataire, statut de paiement, détail de chaque repas avec date et montant, total et option d'envoi par email.",
        category: "facturation",
    },

    // Gestion des données
    {
        id: "liste-familles",
        file: "capture-12.png",
        title: "Gestion des familles",
        description: "Répertoire complet des familles avec nom, email, téléphone, adresse et ville. Filtres et tri disponibles sur chaque colonne.",
        category: "donnees",
    },
    {
        id: "famille-detail",
        file: "capture-13.png",
        title: "Fiche famille",
        description: "Profil détaillé d'une famille : coordonnées, informations du 2ème parent, option de facturation partagée 50/50 et liste des enfants inscrits.",
        category: "donnees",
    },
    {
        id: "liste-enfants",
        file: "capture-14.png",
        title: "Liste des enfants",
        description: "Liste de tous les enfants inscrits avec identifiant, famille, nom, classe et tarif. Possibilité de voir les enfants archivés.",
        category: "donnees",
    },

    // Communication
    {
        id: "message-du-jour",
        file: "capture-04.png",
        title: "Message du jour",
        description: "Éditeur de texte riche pour publier un message du jour visible par tous les parents dès la connexion. Activation et désactivation en un clic.",
        category: "communication",
    },
    {
        id: "messages-accueil",
        file: "capture-05.png",
        title: "Messages page d'accueil",
        description: "Gestion des messages informatifs affichés en permanence sur la page d'accueil des parents : informations générales, paiement, gestion des absences.",
        category: "communication",
    },
    {
        id: "documents-utiles",
        file: "capture-06.png",
        title: "Documents utiles",
        description: "Espace de partage de documents pour les familles : décharges de responsabilité, fiches de renseignements, attestations. Téléchargement PDF et Excel.",
        category: "communication",
    },
    {
        id: "emailings",
        file: "capture-20.png",
        title: "Emailings",
        description: "Composition et envoi d'emails groupés aux familles. Éditeur de texte riche, sélection des destinataires, signature et pied de page configurables.",
        category: "communication",
    },
];

const CATEGORIES: { key: Category; label: string; icon: string }[] = [
    { key: "all", label: "Tout", icon: "📋" },
    { key: "dashboards", label: "Tableaux de bord", icon: "🏠" },
    { key: "gestion", label: "Gestion quotidienne", icon: "📊" },
    { key: "facturation", label: "Facturation", icon: "🧾" },
    { key: "donnees", label: "Données", icon: "👨‍👩‍👧‍👦" },
    { key: "communication", label: "Communication", icon: "📢" },
];

const FEATURED_IDS = ["admin-dashboard", "pointage-du-jour", "feuille-mois", "facturation-auto"];

/* ── Lightbox ── */

function Lightbox({
    screenshot,
    onClose,
    onPrev,
    onNext,
}: {
    screenshot: Screenshot;
    onClose: () => void;
    onPrev: () => void;
    onNext: () => void;
}) {
    const handleKey = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") onPrev();
            if (e.key === "ArrowRight") onNext();
        },
        [onClose, onPrev, onNext],
    );

    useEffect(() => {
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", handleKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", handleKey);
        };
    }, [handleKey]);

    const touchStart = useRef<number | null>(null);

    const onTouchStart = (e: React.TouchEvent) => {
        touchStart.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e: React.TouchEvent) => {
        if (touchStart.current === null) return;
        const diff = e.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(diff) > 50) {
            if (diff > 0) onPrev();
            else onNext();
        }
        touchStart.current = null;
    };

    return (
        <div
            className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
            onClick={onClose}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            <div
                className="relative max-w-5xl w-full flex flex-col items-center"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close */}
                <button
                    onClick={onClose}
                    className="absolute -top-10 right-0 md:-top-12 text-white/80 text-2xl md:text-3xl hover:text-white transition-colors bg-transparent border-none cursor-pointer z-10"
                    aria-label="Fermer"
                >
                    ✕
                </button>

                {/* Image */}
                <div className="w-full flex items-center justify-center rounded-xl overflow-hidden bg-white shadow-2xl">
                    <img
                        src={`/screenshots/${screenshot.file}`}
                        alt={screenshot.title}
                        className="w-full h-auto max-h-[75vh] object-contain"
                    />
                </div>

                {/* Caption */}
                <div className="text-center mt-4 md:mt-6 px-4">
                    <h3 className="text-white text-lg md:text-xl font-bold">
                        {screenshot.title}
                    </h3>
                    <p className="text-white/60 text-sm mt-1 max-w-xl">
                        {screenshot.description}
                    </p>
                </div>

                {/* Navigation arrows */}
                <button
                    onClick={onPrev}
                    className="absolute left-0 md:-left-14 top-1/3 text-white/60 text-4xl md:text-5xl hover:text-white bg-transparent border-none cursor-pointer transition-colors"
                    aria-label="Précédent"
                >
                    ‹
                </button>
                <button
                    onClick={onNext}
                    className="absolute right-0 md:-right-14 top-1/3 text-white/60 text-4xl md:text-5xl hover:text-white bg-transparent border-none cursor-pointer transition-colors"
                    aria-label="Suivant"
                >
                    ›
                </button>
            </div>
        </div>
    );
}

/* ── Screenshot card ── */

function ScreenshotCard({
    screenshot,
    onClick,
}: {
    screenshot: Screenshot;
    onClick: () => void;
}) {
    return (
        <button
            onClick={onClick}
            className="group text-left bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-lg hover:border-[#D3A885] transition-all cursor-pointer p-0 w-full"
        >
            <div className="aspect-video overflow-hidden bg-slate-100">
                <img
                    src={`/screenshots/${screenshot.file}`}
                    alt={screenshot.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                />
            </div>
            <div className="p-4">
                <h3 className="text-sm font-bold text-slate-900 mb-1">{screenshot.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">
                    {screenshot.description}
                </p>
            </div>
        </button>
    );
}

/* ── Full gallery page ── */

export function ScreenshotGallery({
    onNavigate,
    initialScreenshotId,
}: {
    onNavigate: (p: string) => void;
    initialScreenshotId?: string;
}) {
    const [category, setCategory] = useState<Category>("all");
    const [lightboxIdx, setLightboxIdx] = useState<number | null>(() => {
        if (!initialScreenshotId) return null;
        const idx = SCREENSHOTS.findIndex((s) => s.id === initialScreenshotId);
        return idx >= 0 ? idx : null;
    });

    const filtered =
        category === "all"
            ? SCREENSHOTS
            : SCREENSHOTS.filter((s) => s.category === category);

    const openLightbox = (i: number) => setLightboxIdx(i);
    const closeLightbox = () => setLightboxIdx(null);
    const prevLightbox = () => {
        if (lightboxIdx !== null)
            setLightboxIdx((lightboxIdx - 1 + filtered.length) % filtered.length);
    };
    const nextLightbox = () => {
        if (lightboxIdx !== null)
            setLightboxIdx((lightboxIdx + 1) % filtered.length);
    };

    return (
        <section className="pt-[120px] pb-20 px-6 min-h-screen bg-gradient-to-br from-slate-50 via-amber-50 to-orange-50">
            <div className="max-w-[1200px] mx-auto">
                {/* Header */}
                <div className="text-center mb-10">
                    <button
                        onClick={() => onNavigate("home")}
                        className="text-[#D3A885] text-sm font-semibold mb-6 inline-flex items-center gap-1 hover:text-[#c49b78] bg-transparent border-none cursor-pointer"
                    >
                        ← Retour à l'accueil
                    </button>
                    <h1 className="text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold text-slate-900 mb-4">
                        Découvrez l'interface en images
                    </h1>
                    <p className="text-[clamp(1rem,2vw,1.15rem)] text-slate-500 max-w-[680px] mx-auto leading-relaxed">
                        Captures d'écran de l'application réelle, utilisée en production à la cantine de Monoblet depuis 2023.
                    </p>
                </div>

                {/* Category filter */}
                <div className="flex flex-wrap justify-center gap-2 mb-10">
                    {CATEGORIES.map((c) => (
                        <button
                            key={c.key}
                            onClick={() => setCategory(c.key)}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${category === c.key
                                ? "bg-[#D3A885] text-white shadow-md border border-[#D3A885]"
                                : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
                                }`}
                        >
                            {c.icon} {c.label}
                        </button>
                    ))}
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map((s, i) => (
                        <ScreenshotCard
                            key={s.id}
                            screenshot={s}
                            onClick={() => openLightbox(i)}
                        />
                    ))}
                </div>

                {/* Empty state */}
                {filtered.length === 0 && (
                    <p className="text-center text-slate-400 py-12">
                        Aucune capture d'écran dans cette catégorie.
                    </p>
                )}
            </div>

            {/* Lightbox */}
            {lightboxIdx !== null && filtered[lightboxIdx] && (
                <Lightbox
                    screenshot={filtered[lightboxIdx]}
                    onClose={closeLightbox}
                    onPrev={prevLightbox}
                    onNext={nextLightbox}
                />
            )}
        </section>
    );
}

/* ── Home page preview section ── */

export function ScreenshotPreview({
    onNavigate,
    onScreenshotClick,
}: {
    onNavigate: (p: string) => void;
    onScreenshotClick?: (screenshotId: string) => void;
}) {
    const featured = SCREENSHOTS.filter((s) => FEATURED_IDS.includes(s.id));

    return (
        <section className="py-24 px-6 bg-slate-50 scroll-mt-20" id="captures">
            <div className="text-center mb-12">
                <h2 className="text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold text-slate-900 leading-[1.2] tracking-tight mb-4">
                    Voir l'application en action.
                </h2>
                <p className="text-[clamp(1.05rem,2vw,1.25rem)] text-slate-500 max-w-[680px] mx-auto leading-relaxed">
                    Pas de maquette, pas de mockup — voici l'application réelle utilisée quotidiennement par la cantine de Monoblet.
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[1000px] mx-auto mb-10">
                {featured.map((s) => (
                    <div
                        key={s.id}
                        className="group bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-lg hover:border-[#D3A885] transition-all cursor-pointer"
                        onClick={() => onScreenshotClick?.(s.id)}
                    >
                        <div className="aspect-video overflow-hidden bg-slate-100">
                            <img
                                src={`/screenshots/${s.file}`}
                                alt={s.title}
                                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                                loading="lazy"
                            />
                        </div>
                        <div className="p-5">
                            <h3 className="text-base font-bold text-slate-900 mb-1">
                                {s.title}
                            </h3>
                            <p className="text-sm text-slate-500 leading-relaxed">
                                {s.description}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
            <div className="text-center">
                <button
                    onClick={() => onNavigate("screenshots")}
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-[1.05rem] font-bold bg-[#D3A885] text-white shadow-[0_4px_14px_rgba(211,168,133,0.35)] hover:bg-[#c49b78] hover:translate-y-[-2px] hover:shadow-[0_6px_20px_rgba(211,168,133,0.4)] transition-all cursor-pointer border-none"
                >
                    Voir toutes les captures d'écran →
                </button>
            </div>
        </section>
    );
}
